<template>
  <div class="sixtran-search mb-5">
    <h1 class="mb-3">Sixtran Debug Console</h1>
    <p class="text-muted mb-4">
      Showing the 50 most recent sync runs. Use filters to narrow or widen the list, then click a run for details.
    </p>

    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0">Filters</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-3">
            <label class="form-label">Client</label>
            <select v-model="filters.client_id" class="form-select" aria-label="Client">
              <option value="">All Clients</option>
              <option v-for="c in filterOptions.clients" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">Integration / Destination</label>
            <select v-model="filters.census_connection_id" class="form-select" aria-label="Integration / Destination">
              <option value="">All</option>
              <option v-for="cc in filterOptions.census_connections" :key="cc.id" :value="cc.id">{{ cc.name }}</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">Segment</label>
            <select v-model="filters.segment_id" class="form-select" aria-label="Segment">
              <option value="">All Segments</option>
              <option v-for="s in filterOptions.segments" :key="s.id" :value="s.id">
                {{ s.name }}{{ s.client_name ? ` (${s.client_name})` : '' }}
              </option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label">Time Window</label>
            <select v-model="filters.time_window_hours" class="form-select" aria-label="Time Window">
              <option :value="6">Last 6 hours</option>
              <option :value="12">Last 12 hours</option>
              <option :value="24">Last 24 hours</option>
              <option :value="48">Last 48 hours</option>
              <option :value="168">Last 7 days (default)</option>
              <option :value="336">Last 14 days</option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label">Status</label>
            <select v-model="filters.status" class="form-select" aria-label="Status">
              <option value="">Any</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="partial">Partial</option>
            </select>
          </div>
        </div>
        <div class="mt-3">
          <button type="button" class="btn btn-primary" :disabled="loading" @click="search">
            {{ loading ? 'Searching...' : 'Apply Filters' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="rerunMessage" :class="['alert', rerunMessage.type === 'success' ? 'alert-success' : 'alert-danger', 'mb-4']" role="alert">
      {{ rerunMessage.text }}
    </div>

    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">Sync Runs ({{ total }})</h5>
      </div>
      <div class="card-body p-0">
        <div v-if="runs.length === 0" class="p-4 text-center text-muted">
          No runs found. Try adjusting your filters.
        </div>
        <div v-else class="table-responsive">
          <table class="table table-hover mb-0">
            <thead>
              <tr>
                <th>Client</th>
                <th>Segment</th>
                <th>Integration / Destination</th>
                <th>Workflow Execution ID</th>
                <th>Start time</th>
                <th>End time</th>
                <th>Status</th>
                <th>Error</th>
                <th class="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="run in runs"
                :key="run.sync_run_id"
                :class="['clickable-row', { 'table-danger': run.status === 'failed' }]"
                @click="goToRun(run.sync_run_id)"
              >
                <td>{{ run.client_name }}</td>
                <td>{{ run.segment_name }}</td>
                <td>{{ run.integration_name }} / {{ run.destination_name }}</td>
                <td class="font-monospace small">{{ run.workflow_execution_id || '—' }}</td>
                <td>{{ formatDate(run.started_at) }}</td>
                <td>{{ formatDate(run.finished_at) }}</td>
                <td>
                  <span
                    :class="{
                      'badge bg-success': run.status === 'completed',
                      'badge bg-danger': run.status === 'failed',
                      'badge bg-warning': run.status === 'partial',
                      'badge bg-secondary': !['completed', 'failed', 'partial'].includes(run.status),
                    }"
                  >
                    {{ run.status || 'unknown' }}
                  </span>
                </td>
                <td>
                  <div v-if="run.status === 'failed' && (run.error_message || run.error_details)">
                    <span v-if="run.error_message" class="text-danger d-block">
                      {{ truncate(run.error_message, 80) }}
                    </span>
                    <details v-if="run.error_details" class="mt-1">
                      <summary class="small text-muted cursor-pointer">Error details</summary>
                      <pre class="small mt-1 mb-0 p-2 bg-light rounded text-danger overflow-auto" style="max-height: 150px;">{{ run.error_details }}</pre>
                    </details>
                  </div>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="text-end" @click.stop>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    :disabled="rerunningRunId === run.sync_run_id"
                    @click="rerunSync(run)"
                  >
                    {{ rerunningRunId === run.sync_run_id ? 'Rerunning…' : 'Rerun' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { router } from '@inertiajs/vue3';

export default {
  name: 'SixtranSearch',
  props: {
    filterOptions: {
      type: Object,
      default: () => ({}),
    },
    initialRuns: {
      type: Array,
      default: () => [],
    },
    initialTotal: {
      type: Number,
      default: 0,
    },
    initialFilters: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    const initFilters = this.initialFilters || {};
    return {
      filters: {
        client_id: initFilters.client_id ?? '',
        census_connection_id: initFilters.census_connection_id ?? '',
        segment_id: initFilters.segment_id ?? '',
        time_window_hours: initFilters.time_window_hours ?? 168,
        status: initFilters.status ?? '',
      },
      runs: this.initialRuns || [],
      total: this.initialTotal ?? 0,
      loading: false,
      rerunningRunId: null,
      rerunMessage: null,
    };
  },
  watch: {
    initialRuns: {
      handler(val) {
        this.runs = val || [];
      },
      immediate: false,
    },
    initialTotal: {
      handler(val) {
        this.total = val ?? 0;
      },
      immediate: false,
    },
    initialFilters: {
      handler(val) {
        if (val && Object.keys(val).length) {
          this.filters.client_id = val.client_id ?? '';
          this.filters.census_connection_id = val.census_connection_id ?? '';
          this.filters.segment_id = val.segment_id ?? '';
          this.filters.time_window_hours = val.time_window_hours ?? 168;
          this.filters.status = val.status ?? '';
        }
      },
      immediate: false,
      deep: true,
    },
  },
  methods: {
    search() {
      this.loading = true;
      const params = {};
      if (this.filters.client_id) params.client_id = this.filters.client_id;
      if (this.filters.census_connection_id) params.census_connection_id = this.filters.census_connection_id;
      if (this.filters.segment_id) params.segment_id = this.filters.segment_id;
      if (this.filters.status) params.status = this.filters.status;
      if (this.filters.time_window_hours !== 168) params.time_window_hours = this.filters.time_window_hours;

      router.get('/admin/sixtran/search', params, {
        preserveState: true,
        onFinish: () => {
          this.loading = false;
        },
      });
    },
    goToRun(runId) {
      window.location.href = `/admin/sixtran/runs/${runId}`;
    },
    formatDate(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      return d.toLocaleString();
    },
    truncate(str, len) {
      if (!str) return '';
      return str.length <= len ? str : str.substring(0, len) + '…';
    },
    async rerunSync(run) {
      if (!run.client_id || !run.segment_id || !run.census_connection_id) {
        return;
      }
      this.rerunningRunId = run.sync_run_id;
      this.rerunMessage = null;
      try {
        await window.axios.post('/api/census/force_sync', {
          client_id: run.client_id,
          segment_id: run.segment_id,
          census_connection_id: run.census_connection_id,
        });
        this.rerunMessage = { type: 'success', text: 'Sync started successfully.' };
        this.search();
        setTimeout(() => { this.rerunMessage = null; }, 4000);
      } catch (err) {
        this.rerunMessage = {
          type: 'danger',
          text: err.response?.data?.message || err.message || 'Failed to start sync.',
        };
        setTimeout(() => { this.rerunMessage = null; }, 6000);
      } finally {
        this.rerunningRunId = null;
      }
    },
  },
};
</script>

<style scoped>
.clickable-row {
  cursor: pointer;
}
.clickable-row:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
.table-danger:hover {
  background-color: rgba(220, 53, 69, 0.15) !important;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
